import React, { Children, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { compose } from 'redux';
import { connect } from 'react-redux';
import { StyledModal, Header, TitleWrapper } from './style';
import CTA from '../CTA';
import Text from '../Text';
import { withMediaQueries } from '../../../app/hoc/withMediaQueries';
import { MODAL_CLOSE } from '../../../app/redux/actions';
import Tooltips from '../../components/Tooltips';
import Toast from '../../components/Toast';
import { LinearLoader } from '../../components/Loader';

const Modal = ({
  mbTitle = 32,
  mbDescription = 24,
  forceFullscreen = false,
  paddingInline = 48,
  children,
  modalId,
  currentId,
  isOpen,
  modalClose,
  scrollableAreaRef,
  mediaIsPhone,
  title,
  subTitle,
  description,
  mbSubTitle,
  forceOpen = false,
  className = '',
  compact,
  isSurvey,
  isLoading,
}) => {
  const dialogRef = useRef(null);
  const footerRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef?.current;
    if (!dialog) return () => {};

    if (isOpen && modalId === currentId) dialog.showModal();
    else dialog.close();
  }, [currentId, isOpen, modalId]);

  useEffect(() => {
    const dialog = dialogRef?.current;
    if (!dialog) return () => {};

    const handleCancel = (e) => {
      e.preventDefault();
      if (!forceOpen) modalClose();
    };

    dialog.addEventListener('cancel', handleCancel);

    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [forceOpen, modalClose]);

  //close on click on backdrop
  useEffect(() => {
    const dialog = dialogRef?.current;
    if (!dialog) return () => {};

    const handleClick = (e) => {
      const rect = dialogRef.current.getBoundingClientRect();

      const outsideRect =
        e.clientX > 0 &&
        e.clientY > 0 &&
        (rect.left > e.clientX ||
          rect.right < e.clientX ||
          rect.top > e.clientY ||
          rect.bottom < e.clientY);

      if (outsideRect && !forceOpen) {
        modalClose();
      }
    };

    dialog.addEventListener('click', handleClick);

    return () => dialog.removeEventListener('click', handleClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen]);

  useEffect(() => {
    const scrollableArea = scrollableAreaRef?.current;
    if (scrollableArea) {
      if (scrollableArea.scrollHeight > scrollableArea.clientHeight) {
        scrollableArea.classList.add('has-scrollbar');
      } else {
        scrollableArea.classList.remove('has-scrollbar');
      }
    }
  }, [scrollableAreaRef, currentId]);

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  useEffect(() => {
    const handleWindowResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  //calc and set the height of the scrollable-area
  useEffect(() => {
    if (compact) return () => {};
    if (isOpen && modalId === currentId) {
      const dialogRect = dialogRef.current.getBoundingClientRect();
      const scrollableAreaRect =
        scrollableAreaRef.current.getBoundingClientRect();
      const footerRect = footerRef.current.getBoundingClientRect();

      const scrollableAreaTop = scrollableAreaRect.top;
      const dialogMarginBottom = window.innerHeight - dialogRect.bottom;
      const scrollableAreaBottom = footerRect.height + dialogMarginBottom;

      const scrollableAreaHeight =
        window.innerHeight - scrollableAreaTop - scrollableAreaBottom;
      scrollableAreaRef.current.style.height = `${scrollableAreaHeight}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentId, windowHeight]);

  const arrayChildren = Children.toArray(children);

  return isOpen && modalId === currentId ? (
    <StyledModal
      ref={dialogRef}
      className={`${
        (mediaIsPhone && !compact) || forceFullscreen
          ? 'fullscreen'
          : compact
          ? 'compact'
          : ''
      } ${isSurvey ? 'is-survey' : ''} ${className ?? ''}`}
      paddingInline={paddingInline}
    >
      <Header>
        <TitleWrapper>
          <Text type={isSurvey ? 'h1.5' : 'h-modal'} mb={mbTitle}>
            {title}
          </Text>
          {!forceOpen && (
            <CTA
              type="tertiary"
              btnText="Chiudi"
              icon="x-close"
              onClick={modalClose}
              disabled={forceOpen}
            />
          )}
        </TitleWrapper>
        {subTitle && (
          <Text type="h2" color="#646464" mb={mbSubTitle}>
            {subTitle}
          </Text>
        )}
        {description && (
          <Text type="b3" color="#515151" mb={mbDescription}>
            {description}
          </Text>
        )}
      </Header>
      {isLoading && <LinearLoader className="modal-loader" />}
      {Children.map(arrayChildren, (child, i) => {
        const isLast = i === arrayChildren.length - 1;
        return isLast ? <div ref={footerRef}>{child}</div> : child;
      })}
      <Tooltips />
      {isOpen && <Toast />}
    </StyledModal>
  ) : null;
};

Modal.propTypes = {
  modalId: PropTypes.string.isRequired,
  currentId: PropTypes.string,
  children: PropTypes.node,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  description: PropTypes.string,
  mbTitle: PropTypes.number,
  mbSubTitle: PropTypes.number,
  mbDescription: PropTypes.number,
  isOpen: PropTypes.bool,
  modalClose: PropTypes.func,
  mediaIsPhone: PropTypes.bool,
  scrollableAreaRef: PropTypes.object,
  forceFullscreen: PropTypes.bool,
  forceOpen: PropTypes.bool,
  className: PropTypes.string,
  paddingInline: PropTypes.number,
  compact: PropTypes.bool,
  isSurvey: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default compose(
  connect(
    (state) => {
      const {
        modal: { isOpen, id: currentId },
        loader: { isSpinning: isLoading },
      } = state.app;

      return {
        isOpen,
        currentId,
        isLoading,
      };
    },
    (dispatch) => ({
      modalClose: () => dispatch({ type: MODAL_CLOSE }),
    })
  ),
  withMediaQueries
)(Modal);
